'use client';

import { Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Zap, Search, BarChart3, Home as HomeIcon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { HomePanel } from '@/components/HomePanel';
import { FirehosePanel } from '@/components/FirehosePanel';
import { SearchPanel } from '@/components/SearchPanel';
import { AnalyticsPanel } from '@/components/AnalyticsPanel';
import { LiveThroughputBar } from '@/components/LiveThroughputBar';

const VALID_TABS = ['home', 'firehose', 'search', 'analytics'] as const;
type TabValue = typeof VALID_TABS[number];

function HomeContent() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const tabParam = searchParams.get('tab');
  const currentTab: TabValue = VALID_TABS.includes(tabParam as TabValue)
    ? (tabParam as TabValue)
    : 'home';

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', value);
    router.push(`?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="h-7 bg-main text-main-foreground border-b-2 border-border flex items-center justify-center text-xs font-heading tracking-wide">
        FROZEN SNAPSHOT MODE • Data captured February 9, 2026 • Read-only frontend
      </div>
      <Tabs value={currentTab} onValueChange={handleTabChange} className="h-full flex flex-col min-h-0">
        {/* Header + Nav */}
        <header className="bg-secondary-background border-b-2 border-border px-6 py-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36" className="w-9 h-9">
                    {/* Shadow layer */}
                    <path d="M16 4 C8 4 4 10 4 16 C4 22 8 28 16 28 C22 28 26 24 26 18 C26 14 23 11 19 11 C15 11 13 14 13 17 C13 19 14 20 16 20"
                          fill="none" stroke="black" strokeWidth="6" strokeLinecap="round"
                          transform="translate(2, 2)"/>
                    {/* Main stroke with black outline */}
                    <path d="M16 4 C8 4 4 10 4 16 C4 22 8 28 16 28 C22 28 26 24 26 18 C26 14 23 11 19 11 C15 11 13 14 13 17 C13 19 14 20 16 20"
                          fill="none" stroke="black" strokeWidth="6" strokeLinecap="round"/>
                    <path d="M16 4 C8 4 4 10 4 16 C4 22 8 28 16 28 C22 28 26 24 26 18 C26 14 23 11 19 11 C15 11 13 14 13 17 C13 19 14 20 16 20"
                          fill="none" className="stroke-main" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </div>
                <h1 className="text-lg font-heading text-foreground leading-tight">
                  Looper<br />Demo
                </h1>
              </div>
              <TabsList>
                <TabsTrigger value="home">
                  <HomeIcon className="w-4 h-4" />
                  Home
                </TabsTrigger>
                <TabsTrigger value="firehose">
                  <Zap className="w-4 h-4" />
                  Firehose
                </TabsTrigger>
                <TabsTrigger value="search">
                  <Search className="w-4 h-4" />
                  Search
                </TabsTrigger>
                <TabsTrigger value="analytics">
                  <BarChart3 className="w-4 h-4" />
                  Analytics
                </TabsTrigger>
              </TabsList>
            </div>
            <LiveThroughputBar />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <TabsContent value="home" className="flex-1 mt-0 min-h-0 overflow-hidden">
            <div className="h-full bg-background">
              <HomePanel />
            </div>
          </TabsContent>

          <TabsContent value="firehose" className="flex-1 mt-0 min-h-0 overflow-hidden">
            <div className="h-full bg-secondary-background">
              <FirehosePanel />
            </div>
          </TabsContent>

          <TabsContent value="search" className="flex-1 mt-0 min-h-0 overflow-hidden">
            <div className="h-full bg-secondary-background">
              <SearchPanel />
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="flex-1 mt-0 min-h-0 overflow-hidden">
            <div className="h-full bg-background min-h-0">
              <AnalyticsPanel />
            </div>
          </TabsContent>
        </main>
      </Tabs>
    </div>
  );
}

export default function Home() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-screen bg-secondary-background">
        <div className="text-foreground">Loading...</div>
      </div>
    }>
      <HomeContent />
    </Suspense>
  );
}
